import React from 'react';
import {
  IconButton,
  Pane,
  Table,
  TableBodyRow,
  TableBodyRowProps,
  TableColumnType,
  useSorting,
  Text,
} from '@fullstory/components';
import { MODAL_TABLE_MAX_HEIGHT } from '@fullstory/events-ui';
import { FlattenedElementPropertyRule } from '../../types';
import { useElementPropertyRulesTable } from '../../hooks';
import { orderBy } from 'lodash';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { intl } from '@fullstory/intl';
import { ElementPropertyRuleRecord } from '@fullstory/proto/fs/services/search-settings';
import { flattenElementPropertyRules } from '../../utils';
import { prettifyVarType, VarType } from '@fullstory/search-vars';
import { Type } from '@fullstory/proto/fs/api/vars';

const NAME_KEY = 'name';
const PROPERTY_NAME_KEY = 'propertyName';
const DATA_TYPE_KEY = 'dataType';
const LAST_MOD_KEY = 'lastModified';
const CREATED_BY_KEY = 'createBy';

const baseColumns: TableColumnType<FlattenedElementPropertyRule>[] = [
  {
    key: NAME_KEY,
    label: intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' }),
    sortable: true,
    renderCell: elementPropertyRule => (
      <Text appearance="bodySmall" color={elementPropertyRule.archived ? 'disabled' : undefined}>
        {elementPropertyRule.name}
      </Text>
    ),
  },
  {
    key: PROPERTY_NAME_KEY,
    label: intl.formatMessage({ defaultMessage: 'Property Name', id: 'G9Im6D' }),
    sortable: true,
    renderCell: elementPropertyRule => (
      <Text appearance="bodySmall" color={elementPropertyRule.archived ? 'disabled' : undefined}>
        {elementPropertyRule.propertyName}
      </Text>
    ),
  },
  {
    key: DATA_TYPE_KEY,
    label: intl.formatMessage({ defaultMessage: 'Data Type', id: 'Ns1otQ' }),
    sortable: true,
    renderCell: elementPropertyRule => (
      <Text appearance="bodySmall" color={elementPropertyRule.archived ? 'disabled' : undefined}>
        {prettifyVarType(Type[elementPropertyRule.propertyType] as VarType, true)}
      </Text>
    ),
    width: 120,
  },
  {
    key: LAST_MOD_KEY,
    label: intl.formatMessage({ defaultMessage: 'Last Modified', id: 'J8u7Xg' }),
    sortable: true,
    renderCell: elementPropertyRule => {
      const text = () => {
        const lastUpdated = Timestamp.toDate(elementPropertyRule.lastUpdated);
        const lastUpdatedBy = elementPropertyRule.lastUpdatedBy;
        if (!lastUpdated && !lastUpdatedBy) {
          return intl.formatMessage({ defaultMessage: '--', id: 'thiOfy' });
        } else if (!lastUpdated) {
          return intl.formatMessage(
            { defaultMessage: '--, {lastUpdatedBy}', id: '+meCI3' },
            { lastUpdatedBy },
          );
        } else {
          return intl.formatMessage(
            { defaultMessage: '{lastUpdated, date, short}, {lastUpdatedBy}', id: '0glsym' },
            { lastUpdated, lastUpdatedBy },
          );
        }
      };
      return (
        <Text appearance="bodySmall" color={elementPropertyRule.archived ? 'disabled' : undefined}>
          {text()}
        </Text>
      );
    },
  },
  {
    key: CREATED_BY_KEY,
    label: intl.formatMessage({ defaultMessage: 'Created By', id: 'uAfuJA' }),
    sortable: true,
    renderCell: elementPropertyRule => (
      <Text appearance="bodySmall" color={elementPropertyRule.archived ? 'disabled' : undefined}>
        {elementPropertyRule.createdBy}
      </Text>
    ),
  },
];

interface DetailModalElementPropertyRulesTableProps {
  elementPropertyRules: ElementPropertyRuleRecord[];
  canModify: boolean;
  onRowClick: (record: ElementPropertyRuleRecord) => void;
  onRowArchive: (id: string, archive: boolean) => void;
}

export const DetailModalElementPropertyRulesTable = ({
  elementPropertyRules,
  canModify,
  onRowClick,
  onRowArchive,
}: DetailModalElementPropertyRulesTableProps) => {
  const { elementPropertyRulesById } = useElementPropertyRulesTable();
  const flattenedElementPropertyRules = React.useMemo(
    () => flattenElementPropertyRules(elementPropertyRules),
    [elementPropertyRules],
  );

  const columns = React.useMemo(
    () => [
      ...baseColumns,
      {
        key: 'archive',
        label: '',
        renderCell(row) {
          return (
            <IconButton
              title={
                row.archived
                  ? intl.formatMessage({ defaultMessage: 'Unarchive', id: 'ftDYZo' })
                  : intl.formatMessage({ defaultMessage: 'Archive', id: 'hrgo+E' })
              }
              iconName="folder"
              onClick={e => {
                onRowArchive(row.id, !row.archived);
                e.stopPropagation();
              }}
              disabled={!canModify}
            />
          );
        },
      },
    ],
    [baseColumns],
  );

  const sorting = useSorting(flattenedElementPropertyRules, NAME_KEY, {
    sortFunction: (rows, key, direction) => {
      if (key !== LAST_MOD_KEY) return orderBy(rows, key, direction);
      return rows.sort((a, b) => {
        const [dateA, dateB] = [a, b].map(elementPropertyRule =>
          Timestamp.toDate(elementPropertyRule.lastUpdated)?.getTime(),
        );
        if (dateA && dateB) {
          if (direction === 'asc') {
            return dateA - dateB;
          }
          return dateB - dateA;
        }
        return 0;
      });
    },
    defaultSortDirection: 'asc',
  });

  function RowComponent(props: TableBodyRowProps<FlattenedElementPropertyRule>) {
    const { cellProps } = props;
    const modifiedCellProps = { ...cellProps, borders: 'bottom' };
    return <TableBodyRow {...props} cellProps={modifiedCellProps} />;
  }

  return (
    <Pane
      data-component="DetailModalPropertiesTable"
      border="standard"
      maxHeight={MODAL_TABLE_MAX_HEIGHT}
      overflow="auto"
      borderRadius={4}
    >
      <Table
        columns={columns}
        rowKeyAccessor="id"
        borders="none"
        headProps={{ variant: 'dark', rowProps: { cellProps: { fixed: true } } }}
        bodyProps={{
          rowComponent: RowComponent,
          rowProps: {
            onClick: (row: FlattenedElementPropertyRule) => {
              const record = elementPropertyRulesById.get(row.id);
              if (record) {
                onRowClick(record);
              }
            },
          },
        }}
        {...sorting}
      />
    </Pane>
  );
};
